/**
 * =============================================================================
 * NOTICE:
 * 
 * This module, along with its related sub-modules, is being developed and tested
 * in a central repository at https://github.com/palainteractive/web-geocomply-lib
 * 
 * IMPORTANT:
 * Any modifications directly to these source files will not be accepted unless 
 * they are submitted as a pull request, with adaquate documentation and associated 
 * tests, to the central repository:
 * 	https://github.com/palainteractive/web-geocomply-lib
 * 
 * By adhering to this process, we ensure that all changes are properly reviewed 
 * and integrated, maintaining the integrity and consistency of the codebase.
 * 
 * =============================================================================
 */

import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Subject } from 'rxjs';
import { GeoComplyStatus, GeoComplyReasons, GeoComplyHint, GeoComplyAppStores, OobeeOptions } from 'src/app/interfaces/geo-interface';
import { PlatformResponse } from 'src/app/interfaces/webservices-interface';
import { CustomerModel } from 'src/app/models/customer-model';
import { ConfigService } from '../config/config.service';
import { EventsService } from '../event/event.service';
import { UserSessionService } from '../user-session/user-session.service';
import { WebservicesService } from '../webservices/webservices.service';
import { ScriptService } from '../script/script.service';

declare var window:any;

const coerceInt = (a: string | number) => ("string" === typeof a ? parseInt(a) : a);

@Injectable({
    providedIn: 'root'
})

export class GeoComplyNDBSService {

    private geoComplyClient: any;

    private geocomplySubject: Subject<GeoComplyStatus> = new Subject();
    private customer?: CustomerModel;
    
    private hint?: GeoComplyHint;
    private license: any;

    constructor(
        protected config: ConfigService,
        protected platform: Platform,
        protected events: EventsService,
        protected scriptService: ScriptService,
        protected webservices: WebservicesService,
        protected userSession: UserSessionService,
    ){
        this.config.isLoaded().then( () => {
        })

        this.userSession.getCustomer().subscribe((customer) => {
            this.customer = customer;
        });
        this.userSession.refreshCustomer();
    }

    private async checkBrowserPermissions(): Promise<boolean> {
        const permissionStatus = await navigator.permissions.query( { name: 'geolocation' });

        return !( permissionStatus && permissionStatus.state === 'denied');
    }

    public async initialize(): Promise<void> {

        const jsUrl = this.config.getConfig().FEATURE_GEOCOMPLY_NDBS_JS || 'https://stg-cdn.geocomply.com/217/gc-html5.js';
        await this.scriptService.injectLib( jsUrl, 'application/javascript');

        while( !this.geoComplyClient) {
            await new Promise(resolve => setTimeout(resolve, 200));
            try {
                this.geoComplyClient = window.GcHtml5.createClient();
            } catch( err) {
                console.warn( 'GcHtml5: err == ', err);
            }
        }

        this.geoComplyClient.events
        .on('success', (encryptedResponse: string) => {
            this.onGeoComplyPacket( encryptedResponse);
        })
        .on('failed', (code: BigInteger, message: String) => {
            this.onGeoComplyFailure( { code: code, message: message});
        });
        
        if( !await this.checkBrowserPermissions()) {
            this.onGeoComplyFailure( {
                code: 803,
                message: 'Geolocation access denied.',
                troubleshooter_message: 'You have previously denied location access for this site.    Please set the Location access to \'Allowed\' in your browser settings.'
            });
            throw new Error( 'NDBS: Location permission denied.');
        }

        return;
    }

    public getGeoStatuses(): Subject<GeoComplyStatus> {
        return this.geocomplySubject;
    }

    public setLicense( license: string) {
        this.license = license;
    }


    public isGeolocationInProgress(){
        /**
         * @todo
         */
        throw Error( "not implemented");
    }

    protected onGeoComplyPacket(data: string) {
        if( !this.customer) 
            return;
        
        return this.webservices.post('geoComply/data', {
            customer_id: this.customer.customerId,
            session_token: this.userSession.getSessionToken(),
            enc_data: encodeURIComponent(data),
            clientapp: this.config.getClientApp()
        }).toPromise().then((response: PlatformResponse<any>) => {
            console.log('NDBS: geo-comply-ndbs.service.ts: geoStatus: ' + JSON.stringify(response));
            this.events.publish('event:geoComply:dismiss');
            this.events.publish('event:geoComply:dismiss');

            let code = coerceInt(response.payload.error_code);
            let timer = coerceInt(response.payload.geolocate_in);

            this.geocomplySubject.next({
                error_code: code,
                error_message: response.payload.error_message,
                error_details: response.payload.error_details,
                geolocate_in: timer,
                troubleshooter_message: response.payload.troubleshooter_message,
                hint: this.hint
            } as GeoComplyStatus);
        }).catch((e: any) => {
            console.warn('NDBS: geocomply-nodownload.service.ts: EXCEPTION: ', e);
        });
    }

    protected onGeoComplyFailure(err: any){
        console.log( 'GEO: NDBS: failure: ', JSON.stringify( err, null, 2));
        const geoStatus: GeoComplyStatus = {
            error_code: err.code,
            error_message: err.message,
            error_details: '',
            geolocate_in: 0,
            hint: undefined,
        };
        if( err.troubleshooter_message) {
            geoStatus.troubleshooter_message = err.troubleshooter_message;
        }

        this.geocomplySubject.next( geoStatus);
        return;
    }

    public triggerGeoComply( user_id: string, reason: string, license: string) {
        this.config.isLoaded().then( () => {
            const mockLicense = this.config.getConfig().FEATURE_MOCK_LICENSE;

            if( mockLicense) {
                this.geoComplyClient.setLicense( mockLicense);
                console.log( 'GEO: NDBS: using MOCKED license: ', mockLicense);
            } else {
                this.geoComplyClient.setLicense( license);
                console.log( 'GEO: NDBS: using REAL license: ', license);
            }
            this.geoComplyClient.setUserId( user_id);
            this.geoComplyClient.setReason( reason);
            //
            this.geoComplyClient.request();
        });
    }
}
